/**
 * Admin API kliens.
 *
 * A tokent `sessionStorage`-ban tartjuk, nem `localStorage`-ban: a
 * munkamenet a böngészőfül bezárásával véget ér. Egy rendelői gépnél,
 * amit többen használnak, ez a helyes alapértelmezés.
 */

export interface Session {
  token: string;
  email: string;
  display_name: string;
  role: string;
  permissions: string[];
}

const KEY = "kroki-admin-session";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

export function loadSession(): Session | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
}

export function saveSession(session: Session | null): void {
  try {
    if (session) sessionStorage.setItem(KEY, JSON.stringify(session));
    else sessionStorage.removeItem(KEY);
  } catch {
    // Privát ablakban a tárolás tiltott lehet — a munkamenet ilyenkor
    // csak az oldal újratöltéséig él. Ez elfogadható, nem hiba.
  }
}

const FIELD_LABEL: Record<string, string> = {
  email: "Az e-mail cím",
  password: "A jelszó",
  new_password: "Az új jelszó",
  current_password: "A jelenlegi jelszó",
  display_name: "A név",
  role: "A szerepkör",
};

interface FieldError {
  type?: string;
  loc?: unknown[];
  msg?: string;
  ctx?: { min_length?: number; max_length?: number };
}

function fieldMessage(error: FieldError): string {
  const field = String(error.loc?.[error.loc.length - 1] ?? "");
  const label = FIELD_LABEL[field] ?? (field ? `A(z) „${field}” mező` : "Az egyik mező");
  switch (error.type) {
    case "value_error":
      // A szerver saját, magyar üzenete — csak a keretrendszer előtagja nélkül.
      return (error.msg ?? "").replace(/^Value error,\s*/i, "") || `${label} hibás.`;
    case "missing":
      return `${label} megadása kötelező.`;
    case "string_too_short":
      return `${label} legalább ${error.ctx?.min_length ?? "néhány"} karakter legyen.`;
    case "string_too_long":
      return `${label} legfeljebb ${error.ctx?.max_length ?? "ennyi"} karakter lehet.`;
    default:
      return `${label} hibás.`;
  }
}

/**
 * A szerver hibája megjeleníthető szövegként.
 *
 * A `detail` háromféle lehet: szöveg (a végpontok magyar üzenete), a mezők
 * hibáinak listája (422), vagy objektum üzenettel és részletekkel (az
 * árlista-szinkron). A felület mindegyiket nyersen, JSON-ként írta ki — a
 * munkatárs `[{"type":"value_error","loc":["body","email"]…` szöveget látott
 * ahelyett, hogy „Érvénytelen e-mail cím.”
 */
export function describeError(detail: unknown, fallback: string): string {
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const messages = [...new Set(detail.map((d) => fieldMessage(d as FieldError)))];
    return messages.join(" ") || fallback;
  }
  if (detail && typeof detail === "object") {
    const { uzenet, osszegzes, reszletek } = detail as {
      uzenet?: unknown;
      osszegzes?: unknown;
      reszletek?: unknown;
    };
    const parts = [uzenet, osszegzes].filter((p): p is string => typeof p === "string" && !!p);
    if (Array.isArray(reszletek)) parts.push(...reszletek.slice(0, 5).map(String));
    if (parts.length) return parts.join(" · ");
  }
  return fallback;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const session = loadSession();
  const response = await fetch(path, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(session ? { Authorization: `Bearer ${session.token}` } : {}),
      ...(init.headers ?? {}),
    },
  });

  if (response.status === 401) {
    // A lejárt munkamenet nem hiba, hanem állapot: kitakarítjuk, és a
    // felület visszavisz a bejelentkezéshez.
    saveSession(null);
    window.dispatchEvent(new CustomEvent("kroki-logout"));
    throw new ApiError("A munkamenet lejárt, kérjük jelentkezzen be újra.", 401);
  }

  if (!response.ok) {
    let detail = `Hiba (${response.status})`;
    try {
      const body = (await response.json()) as { detail?: unknown };
      detail = describeError(body.detail, detail);
    } catch {
      // A válasz nem JSON — marad az általános üzenet.
    }
    throw new ApiError(detail, response.status);
  }

  return (await response.json()) as T;
}

export const api = {
  async login(email: string, password: string): Promise<Session> {
    const response = await fetch("/admin/v1/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { detail?: unknown };
      throw new ApiError(
        describeError(body.detail, "Nem sikerült a bejelentkezés."),
        response.status,
      );
    }
    const data = (await response.json()) as Session & { expires_in: number };
    const session: Session = {
      token: data.token,
      email: data.email,
      display_name: data.display_name,
      role: data.role,
      permissions: data.permissions,
    };
    saveSession(session);
    return session;
  },

  get: <T>(path: string): Promise<T> => request<T>(path),

  /**
   * Fájl letöltése bejelentkezett kéréssel.
   *
   * Egy sima hivatkozás nem vinné a tokent (az a munkamenet-tárban él, nem
   * sütiben), ezért a fájlt a kliens kéri le, és a böngésző mentésére adja.
   */
  async download(path: string, filename: string): Promise<void> {
    const session = loadSession();
    const response = await fetch(path, {
      headers: session ? { Authorization: `Bearer ${session.token}` } : {},
    });
    if (response.status === 401) {
      saveSession(null);
      window.dispatchEvent(new CustomEvent("kroki-logout"));
      throw new ApiError("A munkamenet lejárt, kérjük jelentkezzen be újra.", 401);
    }
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { detail?: unknown };
      throw new ApiError(describeError(body.detail, `Hiba (${response.status})`), response.status);
    }
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },

  patch: <T>(path: string, body: unknown): Promise<T> =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),

  put: <T>(path: string, body: unknown): Promise<T> =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body) }),

  /**
   * Fájl küldése (Próbapad). A `Content-Type` fejlécet a böngésző állítja be
   * a határolóval együtt — kézzel megadva a szerver nem tudná szétbontani.
   */
  async upload<T>(path: string, form: FormData): Promise<T> {
    const session = loadSession();
    const response = await fetch(path, {
      method: "POST",
      body: form,
      headers: session ? { Authorization: `Bearer ${session.token}` } : {},
    });
    if (response.status === 401) {
      saveSession(null);
      window.dispatchEvent(new CustomEvent("kroki-logout"));
      throw new ApiError("A munkamenet lejárt, kérjük jelentkezzen be újra.", 401);
    }
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { detail?: unknown };
      throw new ApiError(describeError(body.detail, `Hiba (${response.status})`), response.status);
    }
    return (await response.json()) as T;
  },

  /**
   * Saját jelszó cseréje.
   *
   * A szerver minden MÁS munkamenetet megszüntet, ez pedig új tokent kap —
   * a tokent itt azonnal lecseréljük, különben a következő kérés 401-et
   * kapna, és a felhasználó a saját jelszócseréje után kiesne.
   */
  async changeOwnPassword(currentPassword: string, newPassword: string): Promise<void> {
    const result = await request<{ token: string }>("/admin/v1/auth/password", {
      method: "POST",
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
    const session = loadSession();
    if (session) saveSession({ ...session, token: result.token });
  },
  post: <T>(path: string, body?: unknown): Promise<T> =>
    // A `body` kulcsot csak akkor adjuk át, ha van tartalom: az
    // `exactOptionalPropertyTypes` mellett az `undefined` érték nem
    // ugyanaz, mint a hiányzó kulcs.
    request<T>(path, body === undefined
      ? { method: "POST" }
      : { method: "POST", body: JSON.stringify(body) }),
};

/** A szerepkörök emberi neve — a szerver a gépi azonosítót küldi. */
export const ROLE_LABELS: Record<string, string> = {
  adminisztrator: "Adminisztrátor",
  munkatars: "Munkatárs",
  olvaso: "Olvasó",
};

/**
 * Erős, könnyen diktálható kezdőjelszó.
 *
 * Az összetéveszthető karakterek (0/O, 1/l/I) nélkül: a jelszót gyakran
 * szóban vagy papíron adják át az új munkatársnak.
 */
export function generatePassword(length = 14): string {
  const abc = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  const bytes = new Uint32Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => abc[b % abc.length]).join("");
}

export function can(session: Session | null, permission: string): boolean {
  return session?.permissions.includes(permission) ?? false;
}
